import { computeReshape, type ReshapeResult } from './reshapeEngine';

let worker: Worker | null = null;
let workerFailed = false;
let jobSeq = 0;
const pending = new Map<number, { resolve: (r: ReshapeResult) => void; reject: (e: unknown) => void }>();

function inferWorkerUrl(): string | null {
  if (typeof document === 'undefined') return null;
  const scripts = document.getElementsByTagName('script');
  for (let i = scripts.length - 1; i >= 0; i--) {
    const src = scripts[i]?.src;
    if (!src) continue;
    if (src.includes('geoboxgl')) {
      return src.replace(/[^/]+$/, 'reshape.worker.js');
    }
  }
  return null;
}

function getWorker(workerUrl?: string | null): Worker | null {
  if (workerFailed || typeof Worker === 'undefined') return null;
  if (worker) return worker;

  const url = workerUrl || inferWorkerUrl();
  if (!url) {
    workerFailed = true;
    return null;
  }

  try {
    worker = new Worker(url);
    worker.onmessage = (event: MessageEvent<{ id: number; result: ReshapeResult }>) => {
      const job = pending.get(event.data.id);
      if (!job) return;
      pending.delete(event.data.id);
      job.resolve(event.data.result);
    };
    worker.onerror = () => {
      workerFailed = true;
      try {
        worker?.terminate();
      } catch {
        /* ignore */
      }
      worker = null;
      for (const [, job] of pending) {
        job.reject(new Error('reshape worker failed'));
      }
      pending.clear();
    };
    return worker;
  } catch {
    workerFailed = true;
    return null;
  }
}

export function runReshapeCompute(
  targetSnapshot: unknown,
  drawnCoords: number[][],
  allowSingleIntersection: boolean,
  workerUrl?: string | null
): Promise<ReshapeResult> {
  const w = getWorker(workerUrl);

  if (!w) {
    return Promise.resolve(
      computeReshape(targetSnapshot, drawnCoords, allowSingleIntersection)
    );
  }

  const id = ++jobSeq;
  return new Promise<ReshapeResult>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    try {
      w.postMessage({
        id,
        targetSnapshot,
        drawnCoords,
        allowSingleIntersection,
      });
    } catch (err) {
      pending.delete(id);
      resolve(computeReshape(targetSnapshot, drawnCoords, allowSingleIntersection));
    }
  });
}
